import type { CacheSliceConfig } from './types'
import type { AbbreviationsSliceData } from './slices/abbreviations'
import type { RestrictedWordRule } from './slices/restrictedWords'
import type { Settings } from './slices/settings.types'
import { parseSettings, defaultSettings } from './slices/settings.types'
import type { Matter } from './slices/matters'
import type { Task } from './slices/tasks'
import type { Activity } from './slices/activities'
import type { ReportsData } from './slices/reports'
import type { ReportingEntry, ReportingEntriesData } from './slices/reportingEntries'
import type { AuthSliceData } from './slices/auth'

const defaultAuthData: AuthSliceData = {
  userId: '',
  email: '',
  firstName: null,
  lastName: null,
  displayName: '',
  initials: '?',
  isAdmin: false,
  adminPermissions: { overview: false, billing: false, entries: false, calendar: false, support: false },
}

export const cacheSlices = {
  auth: {
    key: 'auth',
    fetch: async () => { throw new Error('auth slice is populated directly, not fetched') },
    initialValue: defaultAuthData as AuthSliceData,
    refetch: 'manual' as const,
  },

  abbreviations: {
    key: 'abbreviations',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/abbreviations', method: 'GET' })
      const data = res.data ?? { enabled: true, abbreviations: [] }
      return {
        enabled: data.enabled,
        abbreviations: (data.abbreviations ?? []).map((a: { id: number; abbreviation: string; expansion: string }) => ({
          id: a.id,
          abbreviation: a.abbreviation,
          expansion: a.expansion,
        })),
      } as AbbreviationsSliceData
    },
    initialValue: { enabled: true, abbreviations: [] } as AbbreviationsSliceData,
    refetch: 'on-focus' as const,
    persistKey: 'cache:abbreviations',
  },

  restricted_words: {
    key: 'restricted_words',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/records/restrictedWords?all=true', method: 'GET' })
      return (res.data ?? []) as RestrictedWordRule[]
    },
    initialValue: [] as RestrictedWordRule[],
    refetch: 'on-focus' as const,
    persistKey: 'cache:restricted_words',
  },

  settings: {
    key: 'settings',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/settings', method: 'GET' })
      return parseSettings(res.data?.[0])
    },
    initialValue: defaultSettings as Settings,
    refetch: 'on-focus' as const,
    persistKey: 'cache:settings',
  },

  matters: {
    key: 'matters',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/matters', method: 'GET' })
      return (res.data ?? []) as Matter[]
    },
    initialValue: [] as Matter[],
    refetch: 'on-focus' as const,
    persistKey: 'cache:matters',
  },

  tasks: {
    key: 'tasks',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/tasks/allTasks', method: 'GET' })
      return (res.data ?? []) as Task[]
    },
    initialValue: [] as Task[],
    refetch: 'on-focus' as const,
  },

  activities: {
    key: 'activities',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/activities', method: 'GET' })
      return (res.data ?? []) as Activity[]
    },
    initialValue: [] as Activity[],
    refetch: 'on-focus' as const,
  },
  reports: {
    key: 'reports',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/user/reports', method: 'GET' })
      return (res.data ?? { managers: [], manages: [] }) as ReportsData
    },
    initialValue: { managers: [], manages: [] } as ReportsData,
    refetch: 'on-focus' as const,
    persistKey: 'cache:reports',
  },
  reporting_entries: {
    key: 'reporting_entries',
    fetch: async (api) => {
      const res = await api.makeApiRequest({ endpoint: '/team/entries', method: 'GET' })
      return {
        entries: (res.data?.entries ?? []) as ReportingEntry[],
        userProfId: (res.data?.userProfId ?? '') as string,
      } as ReportingEntriesData
    },
    initialValue: { entries: [], userProfId: '' } as ReportingEntriesData,
    refetch: 'on-focus' as const,
    persistKey: 'cache:reporting_entries',
  },
} satisfies Record<string, CacheSliceConfig<any>>

export type CacheSliceKey = keyof typeof cacheSlices
export type CacheSliceData<K extends CacheSliceKey> = Awaited<
  ReturnType<(typeof cacheSlices)[K]['fetch']>
>
